import { useState } from 'react';

import { EmptyState } from '../../components/ui/Feedback';
import { FieldError } from '../../components/ui/FieldError';
import { ApiError } from '../../lib/api-client';
import { confirmAction } from '../../lib/confirm-service';
import { useToast } from '../../lib/toast';
import { useAddEmgRegistrationPayment } from './emg.queries';
import { currency, formatEmgDate } from './emg.utils';
import type { EmgPaymentMode, EmgRegistration } from './types';

const paymentModes: EmgPaymentMode[] = ['Cash', 'UPI', 'Transfer to Bank Account', 'Cheque', 'Other', 'Online'];

export const EmgNewReceiptForm = ({ row, onRecorded }: { row: EmgRegistration; onRecorded: () => void }) => {
  const [mode, setMode] = useState<EmgPaymentMode>('Cash');
  const [bankName, setBankName] = useState('');
  const [amount, setAmount] = useState(0);
  const [showAmountError, setShowAmountError] = useState(false);
  const mutation = useAddEmgRegistrationPayment();
  const { showToast } = useToast();
  const dueAmount = row.billing.dueAmount;

  if (dueAmount <= 0) {
    return <EmptyState title="No due on this bill" message="A new receipt can be recorded only when the selected EMG bill has a due amount." />;
  }

  const submit = async () => {
    setShowAmountError(true);
    if (amount <= 0 || amount > dueAmount) return;
    const confirmed = await confirmAction({
      title: 'Confirm Receipt',
      description: `Are you sure you want to add a receipt of ${currency(amount)} to bill #${row.billing.billNumber}?`,
      confirmLabel: 'Yes, Add Receipt',
    });
    if (!confirmed) return;
    mutation.mutate({ id: row.id, payload: { mode, bankName: bankName.trim() || null, amount } }, {
      onSuccess: () => {
        setAmount(0);
        setBankName('');
        setShowAmountError(false);
        showToast('Receipt saved successfully.', 'success');
        onRecorded();
      },
      onError: (error) => showToast(error instanceof ApiError ? error.message : 'The receipt could not be recorded.', 'error'),
    });
  };

  return (
    <>
      <header className="emg-bill-banner">
        <div className="emg-bill-banner-row"><span>New Receipt - {row.patientSnapshot.name}</span></div>
        <p>Bill #{row.billing.billNumber} | Bill Date : {formatEmgDate(row.registrationDate)} | Due {currency(dueAmount)}</p>
      </header>
      <section className="emg-bill-subsection">
        <h3>Record a payment</h3>
        <div className="ipd-daycare-admission-row">
          <label>
            <span className="ipd-daycare-label-text">Bill</span>
            <input value={`Bill #${row.billing.billNumber} - Due ${currency(dueAmount)}`} disabled />
          </label>
          <label><span className="ipd-daycare-label-text">Mode</span><select value={mode} onChange={(event) => setMode(event.target.value as EmgPaymentMode)}>{paymentModes.map((item) => <option key={item}>{item}</option>)}</select></label>
          <label><span className="ipd-daycare-label-text">Bank</span><input value={bankName} onChange={(event) => setBankName(event.target.value)} /></label>
          <label><span className="ipd-daycare-label-text">Amount</span><input type="number" min={0} max={dueAmount} step="0.01" value={amount} aria-invalid={showAmountError && (amount <= 0 || amount > dueAmount)} onChange={(event) => setAmount(Number(event.target.value))} /><FieldError message={showAmountError && amount <= 0 ? 'Amount must be greater than zero.' : showAmountError && amount > dueAmount ? `Amount cannot exceed ${currency(dueAmount)}.` : undefined} /></label>
        </div>
        <div className="emg-form-actions">
          <button className="button button-primary" type="button" disabled={mutation.isPending} onClick={() => void submit()}>{mutation.isPending ? 'Saving...' : 'Add Receipt'}</button>
        </div>
      </section>
    </>
  );
};
